/**
 * Lista das mensagens fixadas do canal.
 *
 * Fixar já gravava certo no servidor, mas não havia onde ver o resultado: a
 * mensagem seguia igual no meio da conversa e não existia lugar nenhum que
 * reunisse as fixadas. Do lado de quem usa, a ação simplesmente não fazia nada.
 */

import { useCallback, useEffect, useState } from 'react'
import { Pin, X } from 'lucide-react'
import { formatTime, type Message } from '@nexus/shared'
import { api, ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import { Avatar, Spinner, Tooltip } from '@/components/ui'
import { MessageContent } from '@/components/chat/MessageContent'

export function PinnedMessages({
  channelId,
  guildId,
  podeGerenciar,
  onIrParaMensagem,
}: {
  channelId: string
  /** Necessário para as menções renderizarem com nome e cor certos. */
  guildId: string | null
  podeGerenciar: boolean
  onIrParaMensagem?: (messageId: string) => void
}) {
  const [aberto, setAberto] = useState(false)
  const [mensagens, setMensagens] = useState<Message[] | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    setErro(null)
    try {
      const resposta = await api.messages.pins(channelId)
      setMensagens(resposta.messages)
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : 'Não foi possível carregar as fixadas')
      setMensagens([])
    }
  }, [channelId])

  useEffect(() => {
    if (aberto) void carregar()
  }, [aberto, carregar])

  // Trocar de canal fecha o painel: a lista aberta seria de outro lugar.
  useEffect(() => {
    setAberto(false)
    setMensagens(null)
  }, [channelId])

  const ir = (messageId: string) => {
    onIrParaMensagem?.(messageId)
    setAberto(false)
  }

  const desafixar = async (messageId: string) => {
    try {
      await api.messages.pin(channelId, messageId, false)
      setMensagens((atual) => atual?.filter((m) => m.id !== messageId) ?? null)
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : 'Não foi possível desafixar')
    }
  }

  return (
    <div className="relative">
      <Tooltip content="Mensagens fixadas">
        <button
          type="button"
          onClick={() => setAberto((v) => !v)}
          aria-label="Mensagens fixadas"
          aria-expanded={aberto}
          className={cn(
            'flex size-8 items-center justify-center rounded-md transition-colors',
            aberto
              ? 'bg-surface-active text-content-primary'
              : 'text-content-secondary hover:bg-surface-hover hover:text-content-primary',
          )}
        >
          <Pin className="size-5" />
        </button>
      </Tooltip>

      {aberto ? (
        <>
          {/* Captura o clique fora sem escurecer a tela: é um painel pequeno,
              não um diálogo que exige atenção exclusiva. */}
          <div className="fixed inset-0 z-40" onClick={() => setAberto(false)} aria-hidden="true" />

          <div className="absolute top-10 right-0 z-50 w-96 max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-lg bg-surface-overlay shadow-2xl animate-fade-in">
            <div className="flex items-center justify-between px-3 py-2.5">
              <span className="text-[13px] font-semibold text-content-primary">
                Mensagens fixadas
              </span>
              <button
                type="button"
                onClick={() => setAberto(false)}
                aria-label="Fechar"
                className="rounded p-0.5 text-content-muted transition-colors hover:text-content-primary"
              >
                <X className="size-4" />
              </button>
            </div>

            <div className="max-h-96 overflow-y-auto px-2 pb-2">
              {mensagens === null ? (
                <div className="flex justify-center py-8">
                  <Spinner className="size-5 text-content-muted" />
                </div>
              ) : erro ? (
                <p className="px-2 py-6 text-center text-[13px] text-danger">{erro}</p>
              ) : mensagens.length === 0 ? (
                <p className="px-4 py-8 text-center text-[13px] leading-relaxed text-content-muted">
                  Nenhuma mensagem fixada por aqui. Clique com o botão direito numa mensagem para
                  fixar a primeira.
                </p>
              ) : (
                mensagens.map((mensagem) => (
                  /*
                    A linha inteira leva até a mensagem, e não um botão pequeno
                    que só aparece ao passar o mouse. Quem abre esta lista está
                    procurando alguma delas; achar e clicar tem que ser o mesmo
                    gesto.
                  */
                  <div
                    key={mensagem.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => ir(mensagem.id)}
                    onKeyDown={(evento) => {
                      if (evento.key === 'Enter' || evento.key === ' ') {
                        evento.preventDefault()
                        ir(mensagem.id)
                      }
                    }}
                    className="group/fixada cursor-pointer rounded-md p-2 text-left transition-colors hover:bg-surface-hover"
                  >
                    <div className="flex items-center gap-2">
                      <Avatar
                        userId={mensagem.authorId}
                        name={mensagem.author.displayName}
                        src={mensagem.author.avatarUrl}
                        size="xs"
                      />
                      <span className="text-[13px] font-medium text-content-primary">
                        {mensagem.author.displayName}
                      </span>
                      <span className="text-[11px] text-content-muted">
                        {formatTime(mensagem.createdAt)}
                      </span>

                      {podeGerenciar ? (
                        <div className="ml-auto opacity-0 transition-opacity group-hover/fixada:opacity-100 [@media(hover:none)]:opacity-100">
                          <button
                            type="button"
                            // Sem isto, desafixar também levaria até a mensagem,
                            // já que a linha toda responde ao clique.
                            onClick={(evento) => {
                              evento.stopPropagation()
                              void desafixar(mensagem.id)
                            }}
                            className="rounded px-1.5 py-0.5 text-[11px] text-content-secondary hover:bg-danger-subtle hover:text-danger"
                          >
                            Desafixar
                          </button>
                        </div>
                      ) : null}
                    </div>

                    <div className="mt-1 pl-7 text-[13px] text-content-secondary">
                      <MessageContent content={mensagem.content} guildId={guildId} />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}
